// Per-bubble readout and the context-change marker. The backend sends token counts only: per
// turn { messageIndex, promptTokens, completionTokens, promptTokensSaved,
// promptTokensWithoutGating, savingsPct, activeState, dispatchToFirstTokenMs }. No models, cached
// tokens, cost or percentiles ever reach the browser.
export function resolveTurnMetric(turnMetrics, index) {
  if (!turnMetrics?.enabled) return undefined;
  return turnMetrics.byMessageIndex?.[index];
}

// The turn with a metric that precedes `index` in message order, or undefined.
function previousTurnMetric(turnMetrics, index) {
  let best;
  for (const t of turnMetrics?.turns ?? []) {
    if (
      typeof t.messageIndex === "number" &&
      t.messageIndex < index &&
      (!best || t.messageIndex > best.messageIndex)
    ) {
      best = t;
    }
  }
  return best;
}

// Characteristics whose active values differ from the previous turn's. Markers start from the
// second turn that carries a state: the first turn has nothing to compare against and would
// otherwise present configured defaults as "detected".
export function resolveContextChanges(turnMetrics, index) {
  const current = resolveTurnMetric(turnMetrics, index);
  if (!current?.activeState) return [];
  const previous = previousTurnMetric(turnMetrics, index);
  if (!previous?.activeState) return [];
  const changes = [];
  const names = new Set([
    ...Object.keys(previous.activeState),
    ...Object.keys(current.activeState),
  ]);
  for (const name of names) {
    const before = [...(previous.activeState[name] ?? [])].sort().join(", ");
    const after = [...(current.activeState[name] ?? [])].sort().join(", ");
    if (before !== after) changes.push(`${name} → ${after || "none"}`);
  }
  return changes;
}

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "–");

// Rendered between the user's bubble and the assistant's reply when detection moved the state.
export function ContextChangeMarker({ changes }) {
  if (!changes?.length) return null;
  return (
    <div className="flex items-center gap-x-3 my-1">
      <div className="flex-1 border-t border-dashed border-gray-300" />
      <p className="text-[11px] text-gray-500 whitespace-nowrap">
        Context detected: {changes.join(" · ")}
      </p>
      <div className="flex-1 border-t border-dashed border-gray-300" />
    </div>
  );
}

export function TurnMetricsLine({ metric, channel }) {
  if (!metric) return null;

  const gated =
    typeof metric.promptTokensWithoutGating === "number" &&
    metric.promptTokensWithoutGating !== metric.promptTokens;
  const parts = [
    gated
      ? `Prompt ${fmt(metric.promptTokens)} (${fmt(metric.promptTokensWithoutGating)} without context mgmt)`
      : `Prompt ${fmt(metric.promptTokens)}`,
  ];

  return (
    <div className="flex justify-end">
      <p className="text-[11px] text-gray-400 mt-1">{parts.join(" · ")}</p>
    </div>
  );
}
